/**
 * Hook for using the solver Web Worker
 *
 * Creates a module worker per operation, publishes progress to
 * `useProgressStore`, resolves the operation's typed result, and terminates
 * the worker after completion, cancellation, failure, or unmount. Starting a
 * new operation cancels the previous one.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useProgressStore, IDLE_PROGRESS } from '@/store/progressStore';
import type {
  SolverOperation,
  SolverOperationParams,
  SolverOperationResult,
  SolverWorkerMessage,
  SolverWorkerRequestFor,
  SolverWorkerResultFor,
  PointPair,
} from '@/types';

/** Run one worker operation; the result type follows the operation name. */
export type RunSolverOperation = <K extends SolverOperation>(
  operation: K,
  points: PointPair[],
  params: SolverOperationParams<K>
) => Promise<SolverOperationResult<K>>;

export interface UseSolverWorkerResult {
  run: RunSolverOperation;
  /** Cancel the current operation; its promise rejects with AbortError. */
  cancel: () => void;
}

const isResultFor = <K extends SolverOperation>(
  message: SolverWorkerResultFor<SolverOperation>,
  operation: K
): message is SolverWorkerResultFor<K> => message.operation === operation;

const setProgress = (progress: number, message: string) =>
  useProgressStore.setState({ isProcessing: true, progress, message });
const finish = (progress: number, message: string) =>
  useProgressStore.setState({ isProcessing: false, progress, message });

export const useSolverWorker = (): UseSolverWorkerResult => {
  const pending = useRef<{
    worker: Worker;
    reject: (reason: Error) => void;
  } | null>(null);

  const stop = useCallback(() => {
    const task = pending.current;
    if (!task) return false;
    pending.current = null;
    task.worker.terminate();
    task.reject(new DOMException('Operation cancelled.', 'AbortError'));
    return true;
  }, []);

  useEffect(
    () => () => {
      if (stop()) useProgressStore.setState(IDLE_PROGRESS);
    },
    [stop]
  );

  const run = useCallback<RunSolverOperation>(
    <K extends SolverOperation>(
      operation: K,
      points: PointPair[],
      params: SolverOperationParams<K>
    ) => {
      stop();
      setProgress(0, 'Starting...');
      return new Promise<SolverOperationResult<K>>((resolve, reject) => {
        let worker: Worker | undefined;
        const fail = (reason: unknown) => {
          worker?.terminate();
          pending.current = null;
          finish(0, 'Error');
          reject(
            reason instanceof Error
              ? reason
              : new Error('Calculation failed. Please retry.')
          );
        };
        try {
          const active = new Worker(
            new URL('../workers/solver.worker.ts', import.meta.url),
            { type: 'module' }
          );
          worker = active;
          pending.current = { worker: active, reject };
          active.onmessage = (event: MessageEvent<SolverWorkerMessage>) => {
            // Ignore late messages from a replaced or cancelled worker.
            if (pending.current?.worker !== active) return;
            const message = event.data;
            switch (message.type) {
              case 'progress':
                setProgress(message.payload.percent, message.payload.message);
                return;
              case 'error':
                fail(new Error(message.payload.message));
                return;
              case 'result':
                if (!isResultFor(message, operation)) {
                  fail(
                    new Error(
                      `Worker answered ${message.operation} for a ${operation} request.`
                    )
                  );
                  return;
                }
                pending.current = null;
                active.terminate();
                finish(100, 'Complete');
                resolve(message.payload);
            }
          };
          active.onerror = (event) => {
            if (pending.current?.worker === active)
              fail(new Error(event.message || 'Worker failed. Please retry.'));
          };
          active.onmessageerror = () => {
            if (pending.current?.worker === active)
              fail(
                new Error('Could not read calculation results. Please retry.')
              );
          };
          const request: SolverWorkerRequestFor<K> = {
            type: operation,
            payload: { points, ...params },
          };
          active.postMessage(request);
        } catch (error) {
          fail(error);
        }
      });
    },
    [stop]
  );

  const cancel = useCallback(() => {
    if (stop()) finish(0, 'Cancelled');
  }, [stop]);

  return useMemo(() => ({ run, cancel }), [run, cancel]);
};
